import React from 'react';
import {MoneyField} from '@/components/common';
import {useAdjustmentStore, StatementType} from '@/store/audit/adjustmentStore';
import type {AuditAdjustmentHeader} from '@/services/auditService/auditAdjustmentService';
import {Stack, MenuItem, Select, FormControl, InputLabel} from '@mui/material';
import {AdjustDialogKeys, AdjustDialogKey} from "@/adjustment/adjust/dialogs/utils/paper-registry";
import type {AdapterRenderContext, PayloadStrategy} from '@/pages/components/audit/adjustment/adapters/types';
import {strategyMap} from '@/pages/components/audit/adjustment/adapters/strategies';

export interface ComponentAdapter {
    key: string; // 例如 'K1', 'QI1'
    match: (ctx:
            AdapterRenderContext) => boolean;
    render: (ctx: AdapterRenderContext) => React.ReactNode;
    // 从 line.details 反序列化为 store entry
    deserialize?: (line: any, detail: any) => {
        statement: StatementType;
        code: string;
        fsId: number;
        adjustmentAmount?: number;
        currency?: string;
        details: Record<string, any>;
    } | null;
}

// strategies are now provided by './strategies'

// IMPORTANT: Hooks must be called from React components, not from arbitrary functions that may run conditionally.
// This component encapsulates the adapter UI and safely uses hooks within a component boundary.
const AdapterMoneyInput: React.FC<{ componentKey: AdjustDialogKey; ctx: AdapterRenderContext }> =
    ({componentKey, ctx}) => {
        const store = useAdjustmentStore();
        const code = String(ctx.code || '').trim();
        const fsId = Number(ctx.item?.fsId);
        const entryKey = `${ctx.statement}|${code}|${fsId}`;
        const current = store.entries[entryKey];
        const strat = (strategyMap[componentKey] || strategyMap.BASIC_ADJUST) as PayloadStrategy;
        const valueFromPayload = strat?.readAmount?.(current?.details) ?? undefined;
        const value = (typeof valueFromPayload === 'number' ? valueFromPayload : current?.adjustmentAmount) ?? 0;
        const currency = ((current as any)?.currency ?? (ctx.item as any)?.currency ?? 'MYR') as string;
        const onCommit = (patch: Partial<{ amount: number; currency: string }>) => {
            const amount = patch.amount ?? value;
            const prevDetails = current?.details as any;
            const details = strat.buildPayload({prev: prevDetails, ctx, amount});
            store.upsertEntry({
                statement: ctx.statement,
                code,
                fsId,
                adjustmentAmount: amount,
                currency: patch.currency ?? currency,
                existingLineId: current?.existingLineId,
                existingDetailId: current?.existingDetailId,
                details: details as any,
            });
        };
        return (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ml: 1}}>
                <MoneyField value={value} showCents editable placeholder={componentKey}
                            onChangeNumber={(amount: number) => onCommit({amount})}/>
                <FormControl size="small" sx={{minWidth: 90}}>
                    <InputLabel>CCY</InputLabel>
                    <Select label="CCY" value={currency} onChange={(e) => onCommit({currency: String(e.target.value)})}>
                        <MenuItem value="MYR">MYR</MenuItem>
                        <MenuItem value="USD">USD</MenuItem>
                        <MenuItem value="HKD">HKD</MenuItem>
                        <MenuItem value="CNY">CNY</MenuItem>
                    </Select>
                </FormControl>
            </Stack>
        );
    };

// 一个极简的金额输入适配器工厂，复用到多种 code（通过策略表驱动）
function moneyInputAdapterFactory(
    componentKey: AdjustDialogKey,
): ComponentAdapter {
    return {
        key: componentKey,
        match: (ctx) => {
            const code = String(ctx.code || '').trim().toUpperCase();
            return code === componentKey.toUpperCase();
        },
        render: (ctx) => {
            // Do not call hooks here; return a component that uses hooks internally.
            return <AdapterMoneyInput componentKey={componentKey} ctx={ctx}/>;
        },
        deserialize: (line: any, detail: any) => {
            if (!detail || String(detail?.component || '').toUpperCase() !== componentKey.toUpperCase()) return null;
            // 使用策略从 payload 中提取金额
            const strat = (strategyMap[componentKey] || strategyMap.BASIC_ADJUST) as PayloadStrategy;
            const amount = strat.readAmount(detail?.payload);
            const statementRaw = String(line?.statement || '').toUpperCase();
            const statement: StatementType = statementRaw.startsWith('P') ? 'PL' : 'BS';
            const detailId = typeof detail?.id === 'number' ? detail.id : (typeof detail?.detailId === 'number' ? detail.detailId : undefined);
            const lineId = typeof line?.id === 'number' ? line.id : (typeof line?.lineId === 'number' ? line.lineId : undefined);
            const currency = line?.currency ?? 'MYR';
            return {
                statement,
                code: String(line?.code || ''),
                fsId: Number(line?.fsId || 0),
                adjustmentAmount: amount,
                currency: String(currency || ''),
                existingLineId: lineId,
                existingDetailId: detailId,
                details: detail?.payload || detail || {},
            };
        },
    };
}

// 适配器注册表由注册表键动态生成，并追加别名 'SALE_INCOME_ADJUST'
const adapters: ComponentAdapter[] = [
    ...AdjustDialogKeys.map(k => moneyInputAdapterFactory(k as AdjustDialogKey)),
];

// 从后端 headers 反序列化为 entries
export function entriesFromHeaders(headers: AuditAdjustmentHeader[]): NonNullable<ReturnType<NonNullable<ComponentAdapter['deserialize']>>>[] {
    const out: NonNullable<ReturnType<NonNullable<ComponentAdapter['deserialize']>>>[] = [];
    for (const h of headers || []) {
        for (const ln of (h?.lines || [])) {
            const details = Array.isArray((ln as any)?.details) ? (ln as any).details : [];
            for (const d of details) {
                for (const adp of adapters) {
                    if (adp.deserialize) {
                        const e = adp.deserialize(ln, d);
                        if (e) out.push(e);
                    }
                }
            }
        }
    }
    return out;
}
